import { IconID } from '@thoughtspot/radiant-react/widgets/icon';
export var SpotlightItemType;
(function (SpotlightItemType) {
    SpotlightItemType["NAVIGATION"] = "navigation";
    SpotlightItemType["CREATE"] = "create";
    SpotlightItemType["ADMIN"] = "admin";
    SpotlightItemType["ADMIN_SUB_ITEM"] = "admin_sub_item";
    SpotlightItemType["EUREKA_OBJECT"] = "eureka_object";
    SpotlightItemType["EUREKA_QUERY"] = "eureka_query";
    SpotlightItemType["SPOTTER_SUGGESTION"] = "spotter_suggestion";
    SpotlightItemType["OBJECT_SEARCH"] = "object_search";
})(SpotlightItemType || (SpotlightItemType = {}));
export const NAVIGATION_ITEMS = [
    {
        id: 'nav-home',
        label: 'Home',
        labelKey: 'homepage.redesign.leftnav.title',
        description: 'Go to home page',
        icon: IconID.THOUGHTSPOT,
        type: SpotlightItemType.NAVIGATION,
        path: '/home',
        keywords: ['dashboard', 'start', 'main'],
    },
    {
        id: 'nav-data',
        label: 'Data',
        labelKey: 'newNavigation.data',
        description: 'Manage data sources and connections',
        icon: IconID.TABLE,
        type: SpotlightItemType.NAVIGATION,
        path: '/data',
        keywords: ['sources', 'tables', 'database', 'connections'],
    },
    {
        id: 'nav-liveboards',
        label: 'Liveboards',
        labelKey: 'homeleftpanel.liveboards',
        description: 'View and manage liveboards',
        icon: IconID.LIVEBOARD_V2,
        type: SpotlightItemType.NAVIGATION,
        path: '/pinboards',
        keywords: ['dashboards', 'reports', 'visualizations'],
    },
    {
        id: 'nav-answers',
        label: 'Answers',
        labelKey: 'homeleftpanel.answers',
        description: 'View saved answers',
        icon: IconID.ANSWER,
        type: SpotlightItemType.NAVIGATION,
        path: '/answers',
        keywords: ['queries', 'search', 'saved'],
    },
    {
        id: 'nav-spotiq',
        label: 'SpotIQ',
        labelKey: 'primaryNavigation.SPOT_IQ',
        description: 'AI-powered insights',
        icon: IconID.CHART_SPOTIQ_ANOMALY_LESS,
        type: SpotlightItemType.NAVIGATION,
        path: '/spotiq',
        keywords: ['ai', 'insights', 'analytics', 'machine learning'],
    },
    {
        id: 'nav-admin',
        label: 'Admin',
        labelKey: 'primaryNavigation.ADMIN',
        description: 'Administration settings',
        icon: IconID.AXIS_SETTINGS,
        type: SpotlightItemType.NAVIGATION,
        path: '/admin',
        keywords: ['settings', 'configuration', 'management'],
    },
    {
        id: 'nav-settings',
        label: 'Settings',
        labelKey: 'metadataListPage.dataManagement.settings.tabHeader',
        description: 'User and system settings',
        icon: IconID.TOOLTIP_SETTINGS,
        type: SpotlightItemType.NAVIGATION,
        path: '/admin/settings',
        keywords: ['preferences', 'configuration', 'options'],
    },
];
export const ACTION_ITEMS = [
    {
        id: 'action-create-liveboard',
        label: 'Create Liveboard',
        description: 'Create a new liveboard',
        icon: IconID.ADD_NOTE_OUTLINE,
        type: SpotlightItemType.CREATE,
        keywords: ['new', 'dashboard', 'add'],
    },
    {
        id: 'action-create-answer',
        label: 'Search Data',
        description: 'Start a new search query',
        icon: IconID.ADD_NOTE_OUTLINE,
        type: SpotlightItemType.CREATE,
        keywords: ['new', 'search', 'query', 'add'],
    },
    {
        id: 'action-create-worksheet',
        label: 'Create Worksheet',
        description: 'Create a new worksheet',
        icon: IconID.ADD_NOTE_OUTLINE,
        type: SpotlightItemType.CREATE,
        keywords: ['new', 'data model', 'add'],
    },
    {
        id: 'action-create-user',
        label: 'Create User',
        description: 'Add a new user',
        icon: IconID.ADD_NOTE_OUTLINE,
        type: SpotlightItemType.CREATE,
        keywords: ['new', 'add', 'member'],
    },
    {
        id: 'action-create-group',
        label: 'Create Group',
        description: 'Create a new user group',
        icon: IconID.GROUP,
        type: SpotlightItemType.CREATE,
        keywords: ['new', 'add', 'team'],
    },
];
export const SPOTLIGHT_CATEGORIES = [
    {
        id: 'navigation',
        title: 'Navigation',
        items: NAVIGATION_ITEMS,
    },
    {
        id: 'actions',
        title: 'Quick Actions',
        items: ACTION_ITEMS,
    },
];
export const getAllSpotlightItems = () => {
    return [...NAVIGATION_ITEMS, ...ACTION_ITEMS];
};
const normalizeCollapsed = (text) => text.toLowerCase().replace(/[\s\-_/()[\],.']+/g, '');
const tokenize = (text) => text
    .toLowerCase()
    .split(/[\s\-_/()[\],.']+/)
    .filter(Boolean);
const levenshtein = (a, b) => {
    if (a === b)
        return 0;
    if (Math.abs(a.length - b.length) > 2)
        return Infinity;
    const row = Array.from({ length: b.length + 1 }, (_, i) => i);
    for (let i = 1; i <= a.length; i++) {
        let prev = row[0];
        row[0] = i;
        for (let j = 1; j <= b.length; j++) {
            const temp = row[j];
            row[j] =
                a[i - 1] === b[j - 1]
                    ? prev
                    : 1 + Math.min(prev, row[j - 1], row[j]);
            prev = temp;
        }
    }
    return row[b.length];
};
const fuzzyThreshold = (len) => {
    if (len <= 3)
        return 0;
    if (len <= 6)
        return 1;
    return 2;
};
export const filterSpotlightItems = (items, query) => {
    if (!query.trim()) {
        return items;
    }
    const normalizedQuery = normalizeCollapsed(query);
    const queryTokens = tokenize(query);
    const getFields = (item) => [
        item.label,
        item.description ?? '',
        ...(item.keywords ?? []),
    ];
    const exactMatch = (item) => getFields(item).some(field => normalizeCollapsed(field).includes(normalizedQuery));
    const fuzzyMatch = (item) => {
        const fieldTokens = getFields(item).flatMap(tokenize);
        return queryTokens.every(qToken => {
            const threshold = fuzzyThreshold(qToken.length);
            if (threshold === 0)
                return false;
            return fieldTokens.some(fToken => {
                if (Math.abs(fToken.length - qToken.length) <= threshold) {
                    return levenshtein(qToken, fToken) <= threshold;
                }
                if (fToken.length > qToken.length) {
                    const prefix = fToken.slice(0, qToken.length + threshold);
                    return levenshtein(qToken, prefix) <= threshold;
                }
                return false;
            });
        });
    };
    const collapsedFuzzyMatch = (item) => {
        if (normalizedQuery.length < 4)
            return false;
        const threshold = fuzzyThreshold(normalizedQuery.length);
        return getFields(item).some(field => {
            const collapsed = normalizeCollapsed(field);
            if (collapsed.length === 0)
                return false;
            if (Math.abs(collapsed.length - normalizedQuery.length) <= threshold) {
                return levenshtein(normalizedQuery, collapsed) <= threshold;
            }
            if (collapsed.length > normalizedQuery.length) {
                const prefix = collapsed.slice(0, normalizedQuery.length + threshold);
                return levenshtein(normalizedQuery, prefix) <= threshold;
            }
            return false;
        });
    };
    return items.filter(item => exactMatch(item) || fuzzyMatch(item) || collapsedFuzzyMatch(item));
};
//# sourceMappingURL=spotlight.types.js.map